import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PaymentDetail } from './PaymentDetail';

// Types (should share with backend but defining here for speed)
type Payment = {
  id: string;
  applicationId: string;
  amount: number;
  method: string;
  status: 'PENDING' | 'PROCESSING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'CANCELLED';
  paidAt?: string;
  createdAt: string;
  application?: {
    receiptNumber?: string;
    applicant?: {
      firstName: string;
      lastName: string;
    };
    offering?: {
      name: string;
    };
  };
};

export const PaymentList = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (statusFilter) query.append('status', statusFilter);

      const res = await fetch(`/api/payments?${query.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = (await res.json()) as { data: Payment[] };
      setPayments(data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleStatusChange = (status: string) => {
    if (status) setSearchParams({ status });
    else setSearchParams({});
  };

  const getStatusBadgeVariant = (status: string) => {
    if (status === 'PAID') return 'default';
    if (status === 'PENDING') return 'outline';
    return 'destructive';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Payment Management</h1>
        <div className="flex gap-2">
          <select
            className="border rounded px-2 py-1"
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PAID">Paid</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Receipt #</TableHead>
              <TableHead>Applicant</TableHead>
              <TableHead>Exam</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Paid At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            )}
            {!loading && payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  No payments found.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{payment.application?.receiptNumber || '-'}</TableCell>
                  <TableCell>
                    {payment.application?.applicant
                      ? `${payment.application.applicant.lastName} ${payment.application.applicant.firstName}`
                      : '-'}
                  </TableCell>
                  <TableCell>{payment.application?.offering?.name || '-'}</TableCell>
                  <TableCell>¥{payment.amount.toLocaleString()}</TableCell>
                  <TableCell>{payment.method}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(payment.status)}>{payment.status}</Badge>
                  </TableCell>
                  <TableCell>{new Date(payment.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>{payment.paidAt ? new Date(payment.paidAt).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedPaymentId(payment.id)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {selectedPaymentId && (
        <PaymentDetail
          paymentId={selectedPaymentId}
          open={!!selectedPaymentId}
          onOpenChange={(open) => !open && setSelectedPaymentId(null)}
          onUpdate={() => {
            fetchPayments();
            setSelectedPaymentId(null);
          }}
        />
      )}
    </div>
  );
};
